import * as THREE from 'three';

// Elite layout definitions
export const QUANTUM_LAYOUTS = {
  galacticSpiral: {
    id: 'galacticSpiral',
    name: '🌌 Galactic Spiral',
    description: 'Golden ratio spiral with orbital rings',
    icon: '🌀',
    complexity: 0.8,
    
    calculatePositions: (entities, type) => {
      const positions = [];
      const goldenAngle = Math.PI * 2 * (1 - 1 / 1.618);
      
      let baseRadius = 40;
      let verticalScale = 15;
      let spiralTightness = 0.3;
      
      if (type === 'function') {
        baseRadius = 25;
        verticalScale = 10;
        spiralTightness = 0.4;
      } else if (type === 'domain') {
        baseRadius = 45;
        verticalScale = 15;
        spiralTightness = 0.35;
      } else if (type === 'system') {
        baseRadius = 65;
        verticalScale = 20;
        spiralTightness = 0.3;
      }
      
      entities.forEach((entity, index) => {
        const angle = index * goldenAngle;
        const radius = baseRadius * Math.sqrt(index + 1) * spiralTightness;
        const height = Math.sin(angle * 3) * verticalScale;
        
        positions.push([
          radius * Math.cos(angle),
          height,
          radius * Math.sin(angle)
        ]);
      });
      
      return positions;
    }
  },
  
  neuralNetwork: {
    id: 'neuralNetwork',
    name: '🧠 Neural Network',
    description: 'Organic brain-like interconnected nodes',
    icon: '🧬',
    complexity: 0.9,
    
    calculatePositions: (entities, type) => {
      const positions = [];
      const total = entities.length;
      
      // Create neural-like clusters
      let clusterCount = 3;
      let clusterRadius = 35;
      
      if (type === 'function') {
        clusterCount = 4;
        clusterRadius = 25;
      } else if (type === 'domain') {
        clusterCount = 3;
        clusterRadius = 45;
      } else if (type === 'system') {
        clusterCount = 2;
        clusterRadius = 60;
      }
      
      entities.forEach((entity, index) => {
        const cluster = index % clusterCount;
        const clusterAngle = (cluster / clusterCount) * Math.PI * 2;
        const clusterCenter = [
          Math.cos(clusterAngle) * clusterRadius,
          (Math.random() - 0.5) * 20,
          Math.sin(clusterAngle) * clusterRadius
        ];
        
        // Organic jitter within cluster
        const jitter = 12;
        positions.push([
          clusterCenter[0] + (Math.random() - 0.5) * jitter,
          clusterCenter[1] + (Math.random() - 0.5) * jitter,
          clusterCenter[2] + (Math.random() - 0.5) * jitter
        ]);
      });
      
      return positions;
    }
  },
  
  crystalLattice: {
    id: 'crystalLattice',
    name: '💎 Crystal Lattice',
    description: 'Perfect geometric lattice structure',
    icon: '🔷',
    complexity: 0.7,
    
    calculatePositions: (entities, type) => {
      const positions = [];
      const total = entities.length;
      
      // Helper function to convert index to 3D coordinates
      const indexTo3D = (idx, gridSize) => {
        const z = Math.floor(idx / (gridSize * gridSize));
        const y = Math.floor((idx % (gridSize * gridSize)) / gridSize);
        const x = idx % gridSize;
        return { x, y, z };
      };
      
      // Crystal lattice parameters
      let latticeType = 'bcc'; // Body-centered cubic
      let unitSize = 20;
      
      if (type === 'function') {
        latticeType = 'fcc';
        unitSize = 15;
      } else if (type === 'domain') {
        latticeType = 'bcc';
        unitSize = 25;
      } else if (type === 'system') {
        latticeType = 'sc';
        unitSize = 30;
      }
      
      entities.forEach((entity, index) => {
        let x, y, z;
        
        if (latticeType === 'sc') {
          // Simple cubic
          const size = Math.cbrt(total) * unitSize;
          const pos = indexTo3D(index, Math.cbrt(total));
          x = (pos.x - 0.5) * size;
          y = (pos.y - 0.5) * size;
          z = (pos.z - 0.5) * size;
        } else if (latticeType === 'bcc') {
          // Body-centered cubic
          const gridSize = Math.cbrt(total * 2);
          const pos = indexTo3D(Math.floor(index / 2), gridSize);
          
          if (index % 2 === 0) {
            // Corner points
            x = (pos.x - 0.5) * unitSize * gridSize;
            y = (pos.y - 0.5) * unitSize * gridSize;
            z = (pos.z - 0.5) * unitSize * gridSize;
          } else {
            // Body centers
            x = (pos.x - 0.5) * unitSize * gridSize + unitSize / 2;
            y = (pos.y - 0.5) * unitSize * gridSize + unitSize / 2;
            z = (pos.z - 0.5) * unitSize * gridSize + unitSize / 2;
          }
        } else {
          // Face-centered cubic (fallback)
          x = (Math.random() - 0.5) * 60;
          y = (Math.random() - 0.5) * 60;
          z = (Math.random() - 0.5) * 60;
        }
        
        // Add slight rotation for visual appeal
        const angle = Math.PI / 6;
        positions.push([
          x * Math.cos(angle) - z * Math.sin(angle),
          y,
          x * Math.sin(angle) + z * Math.cos(angle)
        ]);
      });
      
      return positions;
    }
  },
  
  solarSystem: {
    id: 'solarSystem',
    name: '☀️ Solar System',
    description: 'Planetary orbits with gravitational elegance',
    icon: '🪐',
    complexity: 0.6,
    
    calculatePositions: (entities, type) => {
      const positions = [];
      const total = entities.length;
      
      // Orbital parameters
      let orbitCount = 3;
      let orbitSpacing = 25;
      
      if (type === 'function') {
        orbitCount = 4;
        orbitSpacing = 20;
      } else if (type === 'domain') {
        orbitCount = 3;
        orbitSpacing = 30;
      } else if (type === 'system') {
        orbitCount = 2;
        orbitSpacing = 40;
      }
      
      entities.forEach((entity, index) => {
        const orbitIndex = Math.floor(index / (total / orbitCount));
        const orbitRadius = (orbitIndex + 1) * orbitSpacing;
        const angle = (index % (total / orbitCount)) / (total / orbitCount) * Math.PI * 2;
        
        // Add orbital inclination
        const inclination = Math.PI / 6 * (orbitIndex % 2 === 0 ? 1 : -1);
        
        positions.push([
          orbitRadius * Math.cos(angle) * Math.cos(inclination),
          orbitRadius * Math.sin(inclination),
          orbitRadius * Math.sin(angle) * Math.cos(inclination)
        ]);
      });
      
      return positions;
    }
  },
  
  quantumCloud: {
    id: 'quantumCloud',
    name: '⚛️ Quantum Cloud',
    description: 'Dynamic particle cloud with quantum behavior',
    icon: '☁️',
    complexity: 1.0,
    
    calculatePositions: (entities, type) => {
      const positions = [];
      const total = entities.length;
      
      // Cloud density parameters
      let cloudDensity = 0.8;
      let cloudSize = 50;
      
      if (type === 'function') {
        cloudDensity = 0.9;
        cloudSize = 40;
      } else if (type === 'domain') {
        cloudDensity = 0.7;
        cloudSize = 60;
      } else if (type === 'system') {
        cloudDensity = 0.6;
        cloudSize = 80;
      }
      
      // Create quantum probability distribution
      const center = [0, 0, 0];
      
      entities.forEach((entity, index) => {
        // Quantum probability distribution (normal distribution)
        let attempts = 0;
        let position;
        
        while (attempts < 100) {
          const r = cloudSize * Math.pow(Math.random(), 1/cloudDensity);
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);
          
          position = [
            center[0] + r * Math.sin(phi) * Math.cos(theta),
            center[1] + r * Math.cos(phi),
            center[2] + r * Math.sin(phi) * Math.sin(theta)
          ];
          
          // Check minimum distance from other positions
          let valid = true;
          for (let i = 0; i < positions.length; i++) {
            const dx = position[0] - positions[i][0];
            const dy = position[1] - positions[i][1];
            const dz = position[2] - positions[i][2];
            if (Math.sqrt(dx*dx + dy*dy + dz*dz) < 5) {
              valid = false;
              break;
            }
          }
          
          if (valid) break;
          attempts++;
        }
        
        positions.push(position || [0, 0, 0]);
      });
      
      return positions;
    }
  },

  // NEW: Optimized Thread Layout
  optimized: {
    id: 'optimized',
    name: '⚡ Optimized',
    description: 'Shortest path layout for selected threads with maximum readability',
    icon: '⚡',
    complexity: 0.9,
    
    calculatePositions: (entities, type, selectedThreads = []) => {
      const positions = [];
      const total = entities.length;
      
      // Calculate optimized positions based on thread connections
      if (selectedThreads.length > 0) {
        return calculateOptimizedPositions(entities, selectedThreads);
      }
      
      // Default fallback: radial layout with clustering
      const center = [0, 0, 0];
      const radius = 40;
      const height = 20;
      
      entities.forEach((entity, index) => {
        const angle = (index / total) * Math.PI * 2;
        const r = radius * (0.7 + Math.random() * 0.3);
        
        positions.push([
          center[0] + r * Math.cos(angle),
          center[1] + Math.sin(angle * 2) * height,
          center[2] + r * Math.sin(angle)
        ]);
      });
      
      return positions;
    }
  }
};

// Helper function for optimized layout calculation
export const calculateOptimizedPositions = (entities, selectedThreads) => {
  const positions = new Array(entities.length);
  const visited = new Set();
  
  // First, position entities connected by selected threads
  selectedThreads.forEach((thread, threadIndex) => {
    const connectedEntities = getConnectedEntities(thread, entities);
    
    if (connectedEntities.length > 0) {
      // Create a cluster for this thread
      const clusterRadius = 15 + threadIndex * 5;
      const clusterCenter = [
        Math.cos(threadIndex * Math.PI / 3) * 50,
        0,
        Math.sin(threadIndex * Math.PI / 3) * 50
      ];
      
      // Position connected entities around cluster center
      connectedEntities.forEach((entity, entityIndex) => {
        const entityIdx = entities.indexOf(entity);
        if (entityIdx !== -1 && !visited.has(entityIdx)) {
          const angle = (entityIndex / connectedEntities.length) * Math.PI * 2;
          const r = clusterRadius * (0.5 + entityIndex * 0.1);
          
          positions[entityIdx] = [
            clusterCenter[0] + r * Math.cos(angle),
            clusterCenter[1] + Math.sin(angle) * 5,
            clusterCenter[2] + r * Math.sin(angle)
          ];
          visited.add(entityIdx);
        }
      });
    }
  });
  
  // Position remaining entities in optimized grid
  let gridIndex = 0;
  entities.forEach((entity, index) => {
    if (!positions[index]) {
      const row = Math.floor(gridIndex / 5);
      const col = gridIndex % 5;
      
      positions[index] = [
        (col - 2) * 25,
        0,
        (row - 2) * 25
      ];
      gridIndex++;
    }
  });
  
  return positions;
};

// Calculate optimal layout for selected threads
export const createOptimizedThreadLayout = (departments, threads, layoutId) => {
  const departmentPositions = new Map();
  const threadPositions = new Map();
  
  if (threads.length === 0) {
    // Return default positions if no threads selected
    departments.forEach((dept, index) => {
      const angle = (index / departments.length) * Math.PI * 2;
      const radius = 40;
      departmentPositions.set(dept.id, [
        radius * Math.cos(angle),
        Math.sin(angle * 2) * 10,
        radius * Math.sin(angle)
      ]);
    });
    return { departmentPositions, threadPositions };
  }
  
  // Calculate thread importance weights
  const threadWeights = new Map();
  threads.forEach(thread => {
    const weight = (thread.critical ? 2 : 1) * (thread.importance || 1);
    threadWeights.set(thread.id, weight);
  });
  
  // Group departments by thread connectivity
  const departmentGroups = new Map();
  const departmentThreadCount = new Map();
  
  departments.forEach(dept => {
    const connectedThreads = threads.filter(thread => 
      (thread.connects && thread.connects.includes(dept.name)) ||
      (thread.domains && thread.domains.includes(dept.name)) ||
      (thread.systems && thread.systems.includes(dept.name))
    );
    
    if (connectedThreads.length > 0) {
      departmentThreadCount.set(dept.id, connectedThreads.length);
      
      // Calculate average thread position
      let avgX = 0, avgY = 0, avgZ = 0;
      let totalWeight = 0;
      
      connectedThreads.forEach(thread => {
        const weight = threadWeights.get(thread.id) || 1;
        // Position threads in a circle
        const threadAngle = (Array.from(threadWeights.keys()).indexOf(thread.id) / threads.length) * Math.PI * 2;
        const threadRadius = 30 + (weight * 10);
        
        avgX += Math.cos(threadAngle) * threadRadius * weight;
        avgY += 10 * weight; // Slight elevation for visibility
        avgZ += Math.sin(threadAngle) * threadRadius * weight;
        totalWeight += weight;
      });
      
      if (totalWeight > 0) {
        // Position department near connected threads, but adjust for multiple connections
        const connectionFactor = Math.min(connectedThreads.length, 3);
        const spread = 5 * connectionFactor;
        
        departmentPositions.set(dept.id, [
          avgX / totalWeight + (Math.random() - 0.5) * spread,
          avgY / totalWeight + 5 * connectionFactor,
          avgZ / totalWeight + (Math.random() - 0.5) * spread
        ]);
      }
    }
  });
  
  // Position remaining departments
  departments.forEach(dept => {
    if (!departmentPositions.has(dept.id)) {
      // Position away from main activity
      const index = Array.from(departmentPositions.keys()).length;
      const angle = (index / 10) * Math.PI * 2;
      const radius = 60;
      
      departmentPositions.set(dept.id, [
        radius * Math.cos(angle),
        -10,
        radius * Math.sin(angle)
      ]);
    }
  });
  
  // Calculate thread midpoint positions
  threads.forEach(thread => {
    const connectedDepts = departments.filter(dept => 
      (thread.connects && thread.connects.includes(dept.name)) ||
      (thread.domains && thread.domains.includes(dept.name)) ||
      (thread.systems && thread.systems.includes(dept.name))
    );
    
    if (connectedDepts.length > 0) {
      let avgX = 0, avgY = 0, avgZ = 0;
      let count = 0;
      
      connectedDepts.forEach(dept => {
        const pos = departmentPositions.get(dept.id);
        if (pos) {
          avgX += pos[0];
          avgY += pos[1];
          avgZ += pos[2];
          count++;
        }
      });
      
      if (count > 0) {
        // Position thread label above departments
        threadPositions.set(thread.id, [
          avgX / count,
          avgY / count + 20,
          avgZ / count
        ]);
      }
    }
  });
  
  return { departmentPositions, threadPositions };
};

// Helper function to get connected entities
const getConnectedEntities = (thread, entities) => {
  return entities.filter(entity => 
    (thread.connects && thread.connects.includes(entity.name)) ||
    (thread.domains && thread.domains.includes(entity.name)) ||
    (thread.systems && thread.systems.includes(entity.name))
  );
};


// Layout effect creators
export const createLayoutEffects = {
  galacticSpiral: (scene) => {
    const group = new THREE.Group();
    
    // Add spiral guidance lines
    for (let i = 0; i < 3; i++) {
      const points = [];
      const segments = 100;
      const radius = 30 + i * 20;
      
      for (let j = 0; j <= segments; j++) {
        const angle = (j / segments) * Math.PI * 4;
        const r = radius * Math.sqrt(j / segments);
        points.push(
          new THREE.Vector3(
            r * Math.cos(angle),
            Math.sin(angle * 2) * 5,
            r * Math.sin(angle)
          )
        );
      }
      
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x00f3ff,
        transparent: true,
        opacity: 0.1,
        linewidth: 1
      });
      
      const line = new THREE.Line(geometry, material);
      group.add(line);
    }
    
    scene.add(group);
    return group;
  },
  
  neuralNetwork: (scene) => {
    const group = new THREE.Group();
    
    // Add pulsing neural connections
    const createPulseSphere = (color, size) => {
      const geometry = new THREE.SphereGeometry(size, 8, 8);
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending
      });
      
      return new THREE.Mesh(geometry, material);
    };
    
    for (let i = 0; i < 8; i++) {
      const pulse = createPulseSphere(0x00ff88, 2);
      pulse.position.set(
        (Math.random() - 0.5) * 100,
        (Math.random() - 0.5) * 50,
        (Math.random() - 0.5) * 100
      );
      group.add(pulse);
      
      // Animate pulse
      pulse.userData = {
        phase: Math.random() * Math.PI * 2,
        speed: 0.02 + Math.random() * 0.02,
        scale: 1 + Math.random() * 2
      };
    }
    
    scene.add(group);
    return group;
  },
  
  crystalLattice: (scene) => {
    const group = new THREE.Group();
    const size = 80;
    
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        // Create lattice lines
        const points = [];
        for (let k = -1; k <= 1; k++) {
          points.push(new THREE.Vector3(i * size, j * size, k * size));
        }
        
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
          color: 0x4ecdc4,
          transparent: true,
          opacity: 0.05,
          linewidth: 1
        });
        
        const line = new THREE.Line(geometry, material);
        group.add(line);
      }
    }
    
    scene.add(group);
    return group;
  },
  
  solarSystem: (scene) => {
    const group = new THREE.Group();
    
    // Add orbital rings
    for (let i = 1; i <= 4; i++) {
      const radius = i * 25;
      const ringGeometry = new THREE.RingGeometry(radius - 0.5, radius + 0.5, 64);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0x9370db,
        transparent: true,
        opacity: 0.1,
        side: THREE.DoubleSide
      });
      
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = Math.PI / 2;
      group.add(ring);
    }
    
    // Add central "sun"
    const sunGeometry = new THREE.SphereGeometry(8, 32, 32);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xffd700,
      emissive: 0xffd700,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.7
    });
    
    const sun = new THREE.Mesh(sunGeometry, sunMaterial);
    group.add(sun);
    
    scene.add(group);
    return group;
  },
  
  quantumCloud: (scene) => {
    const group = new THREE.Group();
    
    // Add quantum particle field
    const createQuantumParticleField = (count, radius) => {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const phi = Math.acos(2 * Math.random() - 1);
        const theta = Math.random() * Math.PI * 2;
        const r = radius * Math.pow(Math.random(), 1/3);
        
        positions[i3] = r * Math.sin(phi) * Math.cos(theta);
        positions[i3 + 1] = r * Math.cos(phi);
        positions[i3 + 2] = r * Math.sin(phi) * Math.sin(theta);
        
        const hue = Math.random() * 0.3 + 0.6; // Purple to blue
        const color = new THREE.Color().setHSL(hue, 0.8, 0.6);
        colors[i3] = color.r;
        colors[i3 + 1] = color.g;
        colors[i3 + 2] = color.b;
      }
      
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      
      const material = new THREE.PointsMaterial({
        size: 0.5,
        vertexColors: true,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending
      });
      
      return new THREE.Points(geometry, material);
    };
    
    // Add quantum wave interference pattern
    const createWaveInterferencePattern = () => {
      const waveGroup = new THREE.Group();
      const waveCount = 3;
      
      for (let i = 0; i < waveCount; i++) {
        const waveGeometry = new THREE.SphereGeometry(40 + i * 15, 32, 32);
        const waveMaterial = new THREE.MeshBasicMaterial({
          color: 0x00f3ff,
          wireframe: true,
          transparent: true,
          opacity: 0.05,
          side: THREE.DoubleSide
        });
        
        const wave = new THREE.Mesh(waveGeometry, waveMaterial);
        wave.userData = {
          phase: i * (Math.PI * 2 / waveCount),
          speed: 0.005
        };
        waveGroup.add(wave);
      }
      
      return waveGroup;
    };
    
    const particleField = createQuantumParticleField(500, 60);
    const wavePattern = createWaveInterferencePattern();
    
    group.add(particleField);
    group.add(wavePattern);
    
    scene.add(group);
    return group;
  },

  optimized: (scene, selectedThreadIds = []) => {
    const group = new THREE.Group();
    
    // Add connection optimization visualization
    if (selectedThreadIds.length > 0) {
      // Add shortest path guides
      for (let i = 0; i < selectedThreadIds.length; i++) {
        const angle = (i / selectedThreadIds.length) * Math.PI * 2;
        const radius = 35;
        
        const circleGeometry = new THREE.RingGeometry(radius - 2, radius, 32);
        const circleMaterial = new THREE.MeshBasicMaterial({
          color: 0x00ff00,
          transparent: true,
          opacity: 0.1,
          side: THREE.DoubleSide
        });
        
        const circle = new THREE.Mesh(circleGeometry, circleMaterial);
        circle.rotation.x = Math.PI / 2;
        circle.position.y = 5;
        circle.rotation.z = angle;
        group.add(circle);
      }
      
      // Add optimization text labels
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 128;
      
      context.fillStyle = 'rgba(0, 255, 0, 0.8)';
      context.font = '24px Arial';
      context.textAlign = 'center';
      context.fillText(`Optimized: ${selectedThreadIds.length} threads`, 128, 40);
      context.font = '16px Arial';
      context.fillText('Shortest path calculation', 128, 70);
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(30, 15, 1);
      sprite.position.y = 40;
      group.add(sprite);
    }
    
    scene.add(group);
    return group;
  }
};
